import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | findonlinecollege.com', 'Learn how findonlinecollege.com collects, uses, and protects your personal information.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2069" data-mp="2rraq1dg65t33">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2070" data-mp="2e176iu8ocdhc">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2071" data-mp="2isgulqekr8r4">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2072" data-mp="245489zgy0yzo" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2073" data-mp="290jk6lve4ktg">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2074" data-mp="1v461xaj18fdx">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2075" data-mp="1z6at82m0czxe">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-2076" data-mp="2jngedojdgnwk">
          <p data-id="element-2077" data-mp="2es11ayt6uw17">
            This privacy notice for findonlinecollege.com describes how and why
            we might collect, store, use, and/or share your information when you
            use our services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2078" data-mp="1m07lh8h1kckj">
            <li data-id="element-2079" data-mp="20s001i90hml7">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2080" data-mp="1vy5ba0ejuhf9">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2081" data-mp="13ahtsu8ks6wu">
            <strong className="text-navy" data-id="element-2082" data-mp="13arhgb65e1zd">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* Summary of Key Points */}
        <Section title="Summary of Key Points" data-id="element-2083" data-mp="26r3cqtnj1nc2">
          <SummaryItem label="What personal information do we process?" text="When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use." data-id="element-2084" data-mp="26ps3wff85pe3" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2085" data-mp="1j0h6n7x32d4p" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2086" data-mp="2ugxplb7ujbwb" />
          <SummaryItem label="How do we process your information?" text="To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required." data-id="element-2087" data-mp="nrvrkosgkoi3" />
          <SummaryItem label="When and with whom do we share personal information?" text="Only in specific circumstances (e.g., business transfers or service providers)." data-id="element-2088" data-mp="94aouvzhwrap" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2089" data-mp="1t0oc0a5ol2" />
          <SummaryItem label="What are your rights?" text="Depending on your location, you may have rights under applicable privacy laws." data-id="element-2090" data-mp="3mauaqo2965mg" />
          <SummaryItem label="How do you exercise your rights?" text="You can exercise rights by contacting us (see Section 12)." data-id="element-2091" data-mp="1kio7nmfe6gjv" />
        </Section>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-2092" data-mp="1xi6vkwp73l2q">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2093" data-mp="1xh5fuex8iaeo">
            {tocItems.map(item => <li key={item.id} data-id="element-2094" data-mp="1ikobw8rdjy25">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2095" data-mp="1nkak23scio43">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2096" data-mp="zuygbjfsf896">
          <SubHeading data-id="element-2097" data-mp="14uugt8pa0hjz">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2098" data-mp="qti4r8d8l03d">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </Paragraph>
          <Paragraph data-id="element-2099" data-mp="799q84egq63z">
            <strong className="text-navy" data-id="element-2100" data-mp="7a2ripunk3eq">
              Personal Information Provided by You.
            </strong>{' '}
            The personal information we collect may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses', 'Education level and interests', 'Program preferences']} data-id="element-2101" data-mp="g7lgg5bf31vw" />

          <SubHeading data-id="element-2102" data-mp="13x7g0tvpbj4f">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2103" data-mp="zwdsi1m686c2">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </Paragraph>
          <BulletList items={['IP address', 'Browser and device characteristics', 'Operating system', 'Language preferences', 'Referring URLs', 'Device name and type', 'Pages viewed and time spent', 'Clickstream data']} data-id="element-2104" data-mp="6kpfv8m64790" />
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2105" data-mp="p9m1ko3i1rfm">
          <Paragraph data-id="element-2106" data-mp="ubuw3ow0t1ju">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To facilitate account creation and authentication', 'To deliver and facilitate delivery of services to you', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To fulfill and manage your requests', 'To enable user-to-user communications', 'To request feedback and contact you about your use of our services', 'To send you marketing and promotional communications', 'To deliver targeted advertising to you', 'To protect our services and investigate potential violations', 'To identify usage trends and determine the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2107" data-mp="13ayesrr9zext" />

          <SubHeading data-id="element-2108" data-mp="36hdv38yx9rpc">Email Communications</SubHeading>
          <Paragraph data-id="element-2109" data-mp="32hn71d0s5poi">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from findonlinecollege.com and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2110" data-mp="3q783zngd09op">
              contact@findonlinecollege.com
            </span>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2111" data-mp="hq3jb7oo7og0">
          <SubHeading data-id="element-2112" data-mp="3zi2dois1okr">How We Collect SMS Consent</SubHeading>
          <Paragraph data-id="element-2113" data-mp="8xt93qdrziyr">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from findonlinecollege.com and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2114" data-mp="1kipm8ugof6mt">How We Use Your Phone Number for SMS</SubHeading>
          <Paragraph data-id="element-2115" data-mp="1fkxwa5zldul9">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2116" data-mp="rvljyx8yx6eq" />

          <SubHeading data-id="element-2117" data-mp="ww9brl50tra0">Consent Is Not Required</SubHeading>
          <Paragraph data-id="element-2118" data-mp="2d7db100mwe54">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </Paragraph>

          <SubHeading data-id="element-2119" data-mp="1bklwnqp6lss4">Message Frequency and Rates</SubHeading>
          <Paragraph data-id="element-2120" data-mp="1bl3xl0msgv6d">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </Paragraph>

          <SubHeading data-id="element-2121" data-mp="xsxkxoiuh6bo">How to Opt Out</SubHeading>
          <Paragraph data-id="element-2122" data-mp="xtp4g0z63dll">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', 'Contacting us directly using the information in Section 13']} data-id="element-2123" data-mp="nurlddkigc9z" />
          <Paragraph data-id="element-2124" data-mp="2i9anhf154drh">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2125" data-mp="2lovlsflt2yqj">For Assistance</SubHeading>
          <Paragraph data-id="element-2126" data-mp="2pr8iyfmhlobp">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section 13.
          </Paragraph>

          <SubHeading data-id="element-2127" data-mp="2zicuf5cyer1s">Third-Party SMS Senders</SubHeading>
          <Paragraph data-id="element-2128" data-mp="2zjxqizex2i1r">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2129" data-mp="2gpir58op0nde">Record Retention</SubHeading>
          <Paragraph data-id="element-2130" data-mp="3n8gx9aibimrq">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2131" data-mp="2xiqn6ndv7ng3">
          <Paragraph data-id="element-2132" data-mp="37hze17dfoly8">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2133" data-mp="2jrlvc7gqnzgb">
            {[{
            label: 'Business Transfers',
            text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
          }, {
            label: 'Educational Institutions',
            text: 'We share your information with schools and educational partners that match your interests.'
          }, {
            label: 'Service Providers',
            text: 'With third-party vendors, service providers, contractors, or agents who perform services for us.'
          }, {
            label: 'Advertising Partners',
            text: 'With advertising partners to deliver targeted ads.'
          }, {
            label: 'Legal Requirements',
            text: 'When required by law or to protect rights, safety, and property.'
          }].map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2134" data-mp="uc1d07i1yjdl">
                <span className="text-navy mt-2 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2135" data-mp="z807mctz4xmg" />
                <span data-id="element-2136" data-mp="lcebzmt8jfl2">
                  <strong className="text-navy" data-id="element-2137" data-mp="pbn64ss6tfh5">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2138" data-mp="3a2e5s1zpl24r">
          <Paragraph data-id="element-2139" data-mp="3qbd8wkdv28k6">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </Paragraph>
        </NumberedSection>

        {/* Section 6 */}
        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2140" data-mp="3bm7fxe5m9ekq">
          <Paragraph data-id="element-2141" data-mp="83xchvw8y9u1">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </Paragraph>
        </NumberedSection>

        {/* Section 7 */}
        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2142" data-mp="2jtgf0qczn1p5">
          <Paragraph data-id="element-2143" data-mp="zcgyuvd46fdi">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </Paragraph>
        </NumberedSection>

        {/* Section 8 */}
        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2144" data-mp="1i3rshka85693">
          <Paragraph data-id="element-2145" data-mp="1r1tua21s82xf">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </Paragraph>
        </NumberedSection>

        {/* Section 9 */}
        <NumberedSection id="section-9" number={9} title="What Are Your Privacy Rights?" data-id="element-2146" data-mp="14yl31zelazlj">
          <Paragraph data-id="element-2147" data-mp="1dyzp50zdoiq3">
            Depending on your location, you may have the following rights:
          </Paragraph>
          <BulletList items={['The right to access your personal information', 'The right to request correction of your personal information', 'The right to request deletion of your personal information', 'The right to opt out of marketing communications', 'The right to opt out of the sale or sharing of personal information (where applicable)']} data-id="element-2148" data-mp="18zerctvy2gey" />
          <Paragraph data-id="element-2149" data-mp="1sq09a39hm122">
            To exercise these rights, please contact us using the information in
            Section 13.
          </Paragraph>

          <SubHeading data-id="element-2150" data-mp="21pd7u0w2p580">Opt Out of Marketing Email</SubHeading>
          <Paragraph data-id="element-2151" data-mp="21mr0a196mpps">
            To opt out of marketing email: Click the unsubscribe link in any
            marketing email you receive from us, or contact us at the address in
            Section 13. Requests are processed within 10 business days.
          </Paragraph>

          <SubHeading data-id="element-2152" data-mp="2bbobrk5wu0x9">
            California Residents — Opt Out of Sale/Sharing
          </SubHeading>
          <Paragraph data-id="element-2153" data-mp="2g9g0y6f678xr">
            California residents — opt out of sale/sharing of personal
            information: To exercise your right to opt out under CCPA/CPRA,
            contact us at the address in Section 13 with the subject line{' '}
            <strong className="text-navy" data-id="element-2154" data-mp="1sm85eg7jne8x">"California Privacy Opt-Out."</strong>{' '}
            We will process your request within 15 business days.
          </Paragraph>
        </NumberedSection>

        {/* Section 10 */}
        <NumberedSection id="section-10" number={10} title="Your California Privacy Rights (CCPA/CPRA)" data-id="element-2155" data-mp="1jmbbo06arp70">
          <Paragraph data-id="element-2156" data-mp="1nog2ys99w9qh">
            This section applies only to California residents. Under the
            California Consumer Privacy Act (CCPA), as amended by the California
            Privacy Rights Act (CPRA), you have specific rights regarding your
            personal information.
          </Paragraph>

          <SubHeading data-id="element-2157" data-mp="1p53xrgwcsynx">Categories of Personal Information Collected</SubHeading>
          <Paragraph data-id="element-2158" data-mp="1p77zbs6s5ll1">
            In the preceding 12 months, we have collected the following
            categories of personal information:
          </Paragraph>
          <BulletList items={['Identifiers (e.g., name, email address, phone number, IP address)', 'Personal information categories listed in the California Customer Records statute (e.g., contact information)', 'Internet or other similar network activity (e.g., browsing history, interaction with our website)', 'Education and program-interest information (e.g., degree level, area of study)', 'Inferences drawn from other personal information (e.g., preferences and characteristics)']} data-id="element-2159" data-mp="1gbrz8tffma70" />

          <SubHeading data-id="element-2160" data-mp="3d1bev3uk5jva">How We Use and Share Your Information</SubHeading>
          <Paragraph data-id="element-2161" data-mp="3i17nxhi441hp">
            We use this information to provide our services, match you with
            educational programs, and for marketing purposes. We may share this
            information with the following categories of third parties:
          </Paragraph>
          <BulletList items={['Educational partners (to fulfill your requests for information)', 'Advertising partners (to deliver targeted advertisements)', 'Service providers (to operate our website and services)']} data-id="element-2162" data-mp="2uc4tta6akg6b" />

          <SubHeading data-id="element-2163" data-mp="26mka2p2bdevz">Your CCPA/CPRA Rights</SubHeading>
          <BulletList items={['Right to Know/Access: You can request information about the categories and specific pieces of personal information we have collected about you.', 'Right to Delete: You can request that we delete your personal information, subject to certain exceptions.', 'Right to Correct: You can request that we correct inaccurate personal information we hold about you.', 'Right to Opt-Out: You have the right to opt out of the sale or sharing of your personal information.', 'Right to Limit Use: You have the right to limit the use and disclosure of sensitive personal information (note: we do not generally collect sensitive personal information).', 'Right to Non-Discrimination: We will not discriminate against you for exercising any of your CCPA rights.']} data-id="element-2164" data-mp="26lr8e2i7pfkp" />

          <SubHeading data-id="element-2165" data-mp="1fj5vpi9s5x9u">How to Submit a Request</SubHeading>
          <Paragraph data-id="element-2166" data-mp="i2qndm533kaw">
            To exercise your Right to Know, Delete, or Correct, please contact
            us using the information in Section 13. To exercise your Right to
            Opt-Out of the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2167" data-mp="2wa9eyhpd3poj">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
          <Paragraph data-id="element-2168" data-mp="2maqywcckoo1r">
            We will confirm receipt of your request within 10 business days and
            aim to respond within 45 days. If we require more time, we will
            inform you of the reason and extension period in writing. You may
            also use an authorized agent to submit a request on your behalf,
            provided you supply written permission and verify your own identity
            directly with us.
          </Paragraph>
        </NumberedSection>

        {/* Section 11 */}
        <NumberedSection id="section-11" number={11} title="Controls for Do-Not-Track Features" data-id="element-2169" data-mp="1ylpxh925qd5z">
          <Paragraph data-id="element-2170" data-mp="1jziqbsrg5u28">
            Most web browsers and some mobile operating systems include a
            Do-Not-Track ("DNT") feature or setting you can activate to signal
            your privacy preference. At this stage, no uniform technology
            standard for recognizing and implementing DNT signals has been
            finalized.
          </Paragraph>
        </NumberedSection>

        {/* Section 12 */}
        <NumberedSection id="section-12" number={12} title="Do We Make Updates to This Notice?" data-id="element-2171" data-mp="22pidub7n313j">
          <Paragraph data-id="element-2172" data-mp="27olivvnmd7bi">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            "Last updated" date at the top of this privacy notice.
          </Paragraph>
        </NumberedSection>

        {/* Section 13 */}
        <NumberedSection id="section-13" number={13} title="How Can You Contact Us About This Notice?" data-id="element-2173" data-mp="2lmrxntgvmglb">
          <Paragraph data-id="element-2174" data-mp="ezuaya1pvuhe">
            If you have questions or comments about this notice, please contact
            us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2175" data-mp="a19b4bqdncth">
            <p className="font-bold text-navy text-lg" data-id="element-2176" data-mp="opvz11u2lcwb">13th Floor Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2177" data-mp="jr2kr76ylhv3">
              2870 S Federal Hwy., Suite 237, Deerfield Beach, FL 33441
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2178" data-mp="3s7e6v99uejwq">
              Email:{' '}
              <a href="mailto:contact@findonlinecollege.com" className="text-navy underline hover:text-crimson font-medium" data-id="element-2179" data-mp="3n949qt4k598h">
                contact@findonlinecollege.com
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14" data-id="element-2180" data-mp="3i4jyza8875s5">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-2181" data-mp="3i5br2b2m6ldo">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-2182" data-mp="3n3wvkmowfr7x">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-2183" data-mp="3w1yu8vl5u7s1">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-2184" data-mp="4vxo91g1y4d9">
        <span className="text-crimson font-bold text-xl" data-id="element-2185" data-mp="9vtii8yy6hfm">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-2186" data-mp="en1qr1ojjwr7">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-2187" data-mp="invhs3xa5rob">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-2188" data-mp="so4fxnau1n6c">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-2189" data-mp="xs6jgmfoh86b">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2190" data-mp="ssbuiofgqsnw">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-2191" data-mp="osa275vmbbla">
          {item}
        </li>)}
    </ul>;
}
function SummaryItem({
  label,
  text
}: {
  label: string;
  text: string;
}) {
  return <div className="border-l-2 border-crimson/40 pl-4 py-1" data-id="element-2192" data-mp="1giej2uk65ghr">
      <p className="text-gray-700 leading-relaxed" data-id="element-2193" data-mp="1gl0rsxisq21k">
        <strong className="text-navy block mb-1" data-id="element-2194" data-mp="1bqn1h9izql3r">{label}</strong>
        {text}
      </p>
    </div>;
}
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'What are your privacy rights?'
}, {
  id: 'section-10',
  label: 'Your California Privacy Rights (CCPA/CPRA)'
}, {
  id: 'section-11',
  label: 'Controls for do-not-track features'
}, {
  id: 'section-12',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-13',
  label: 'How can you contact us about this notice?'
}];